/*
 * Cart "save my bag" email capture (see snippets/cart-save-bag.liquid). Live since 2026-09-14.
 * The form is injected into the cart notification after page load, so this listens with event delegation.
 * Klaviyo client-side endpoints use the PUBLIC site id only (never a private key):
 *   1. POST /client/subscriptions  -> Email List T6gyac with email marketing consent (same list as the popup)
 *   2. POST /client/events         -> metric "Saved Bag" carrying the cart items, a cart-restore link and the traffic
 *                                     source, so the Pinterest checkpoint can count bags saved by ad visitors whose
 *                                     checkout later arrives through the email link instead of the tagged visit.
 * This file loads on every page (snippets/cart-notification.liquid), so it also records the landing source, and
 * credits sign-ups from the Shopify Forms popup to that source (see "Shopify Forms sign-up popup" below).
 * It also turns browser autofill on in the popup and offers a "Did you mean ...?" line for mistyped mail domains
 * in both email fields (see "Email entry help" below).
 */
(() => {
  const COMPANY_ID = 'SMDLVF';
  const LIST_ID = 'T6gyac';
  const REVISION = '2026-07-15';
  const BASE = 'https://a.klaviyo.com/client';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const TOUCH_SESSION_KEY = 'museori_touch_session';
  const TOUCH_LAST_KEY = 'museori_touch_last';
  const OWN_HOSTS = /(^|\.)(museori\.com|myshopify\.com|shopify\.com|shop\.app)$/;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

  const loadJSON = (area, key) => {
    try {
      return JSON.parse(window[area].getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  };

  const saveJSON = (area, key, value) => {
    try {
      window[area].setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage blocked (private mode, site data off): the bag still saves, only without a source.
    }
  };

  // A landing counts when it carries UTM tags, a Pinterest click id (epik), or an external referrer
  // (pinterest.com, android-app://com.pinterest, google...). Internal page views return null.
  const readTouch = (href, referrer) => {
    const url = new URL(href);
    let referrerHost = '';
    try {
      referrerHost = referrer ? new URL(referrer).host : '';
    } catch (error) {
      referrerHost = String(referrer).slice(0, 100);
    }
    const external = Boolean(referrerHost) && !OWN_HOSTS.test(referrerHost);
    const touch = {};
    UTM_KEYS.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value) touch[key] = value.slice(0, 200);
    });
    const clickId = url.searchParams.has('epik');
    // Paid Google clicks carry gclid / gbraid / wbraid; free listings share their UTMs and carry none.
    const googleClickId = ['gclid', 'gbraid', 'wbraid'].some((key) => url.searchParams.has(key));
    if (!Object.keys(touch).length && !external && !clickId && !googleClickId) return null;
    return {
      ...touch,
      referrer_host: external ? referrerHost : '',
      pinterest_click_id: clickId,
      google_click_id: googleClickId,
      google_campaign_id: (url.searchParams.get('gad_campaignid') || '').slice(0, 30),
      landing_path: url.pathname,
      at: new Date().toISOString(),
    };
  };

  try {
    const touch = readTouch(window.location.href, document.referrer);
    if (touch) {
      if (!loadJSON('sessionStorage', TOUCH_SESSION_KEY)) saveJSON('sessionStorage', TOUCH_SESSION_KEY, touch);
      saveJSON('localStorage', TOUCH_LAST_KEY, touch);
    }
  } catch (error) {
    // Never let source tracking break the page.
  }

  // First external landing of this browser session; else the most recent one on this device.
  const sourceProperties = () => {
    const session = loadJSON('sessionStorage', TOUCH_SESSION_KEY);
    const touch = session || loadJSON('localStorage', TOUCH_LAST_KEY);
    if (!touch) return { source_scope: 'none' };
    return {
      source_scope: session ? 'this_session' : 'earlier_visit',
      utm_source: touch.utm_source || '',
      utm_medium: touch.utm_medium || '',
      utm_campaign: touch.utm_campaign || '',
      utm_content: touch.utm_content || '',
      referrer_host: touch.referrer_host || '',
      pinterest_click_id: Boolean(touch.pinterest_click_id),
      google_click_id: Boolean(touch.google_click_id),
      google_campaign_id: touch.google_campaign_id || '',
      landing_path: touch.landing_path || '',
      source_at: touch.at || '',
    };
  };

  const post = async (path, body) => {
    const res = await fetch(`${BASE}/${path}/?company_id=${COMPANY_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Klaviyo ${path} ${res.status}`);
    return res;
  };

  /*
   * Email entry help (2026-10-12). One shopper typed Skybellamom@gmaol.com into the popup and the right address into
   * the save-bag field 43 s later, which made her two subscribers. gmaol.com has a live mail server, so mail to a
   * typo does not even bounce. A near miss of a common mail provider gets a "Did you mean ...?" line under the field
   * as the shopper types. If they submit before it shows, that first submit is held once so they see it; a second
   * submit with the same address goes through. The address only changes when they tap the suggestion.
   * v2 the same day (owner test "gmli,com" got nothing): commas, semicolons and spaces count as a dot, and the
   * provider name is matched on its own with up to 2 slips (gmli -> gmail), so the ending is judged separately.
   */
  // Provider name -> its own endings. '*' = it also runs country versions (yahoo.se, hotmail.fr) or is an ISP whose
  // staff use .com (att.com), so any real ending typed after the exact name is left alone.
  const MAIL_PROVIDERS = {
    gmail: ['com'], googlemail: ['com'], icloud: ['com'], me: ['com'], mac: ['com'], ymail: ['com'],
    rocketmail: ['com'], msn: ['com'], aim: ['com'], email: ['com'], protonmail: ['com', 'ch'], proton: ['me'],
    yahoo: ['com', '*'], hotmail: ['com', '*'], outlook: ['com', '*'], live: ['com', '*'], aol: ['com', '*'],
    gmx: ['com', '*'], mail: ['com', '*'], comcast: ['net', '*'], att: ['net', '*'], verizon: ['net', '*'],
    sbcglobal: ['net'], cox: ['net'], charter: ['net'], bellsouth: ['net'], earthlink: ['net'],
  };
  const SUGGEST_DELAY_MS = 400;
  // Real endings. .co and .cm are left out on purpose: they are the commonest slips for .com.
  const REAL_ENDINGS = new Set(['com', 'net', 'org', 'edu', 'gov', 'me', 'ca', 'fr', 'de', 'it', 'es', 'nl', 'be', 'ch',
    'at', 'se', 'no', 'dk', 'fi', 'ie', 'pl', 'pt', 'jp', 'kr', 'cn', 'br', 'mx', 'in', 'tw', 'hk', 'sg', 'ae', 'ru',
    'co.uk', 'com.au', 'co.nz', 'co.jp', 'co.in', 'com.br', 'com.mx', 'com.sg', 'com.hk', 'com.tw']);

  // Edit distance where swapping two neighbouring letters counts as one edit (gmial -> gmail).
  const editDistance = (a, b) => {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j += 1) d[0][j] = j;
    for (let i = 1; i <= a.length; i += 1) {
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  };

  const suggestEmail = (value) => {
    const email = (value || '').trim();
    const at = email.lastIndexOf('@');
    if (at < 1) return null;
    const typed = email.slice(at + 1).toLowerCase();
    const domain = typed.replace(/[\s,;:]+/g, '.').replace(/\.{2,}/g, '.').replace(/^\.+|\.+$/g, '');
    if (!domain) return null;
    const dot = domain.indexOf('.');
    const name = dot > 0 ? domain.slice(0, dot) : domain;
    const ending = dot > 0 ? domain.slice(dot + 1) : '';
    let best = null;
    let bestCost = Infinity;
    Object.entries(MAIL_PROVIDERS).forEach(([provider, endings]) => {
      const nameCost = editDistance(name, provider);
      const limit = provider.length <= 2 ? 0 : provider.length === 3 ? 1 : 2;
      if (nameCost > limit) return;
      const own = endings.filter((e) => e !== '*');
      const realOther = ending && REAL_ENDINGS.has(ending) && !own.includes(ending);
      let target;
      if (!realOther) {
        target = own.includes(ending) ? ending
          : own.reduce((a, b) => (editDistance(ending, a) <= editDistance(ending, b) ? a : b));
        if (ending && editDistance(ending, target) > 2) return;
      } else if (endings.includes('*') && (nameCost === 0 || provider.length >= 5)) {
        target = ending;            // yahoo.se stays; hotmial.fr -> hotmail.fr
      } else if (nameCost === 0 && !endings.includes('*')) {
        target = own[0];            // gmail.net -> gmail.com: Gmail has no other ending
      } else {
        return;                     // msu.edu is not msn, uol.com.br is not aol
      }
      const endingCost = target === ending ? 0 : (ending ? editDistance(ending, target) : 1);
      const cost = nameCost * 10 + endingCost;   // the closer name wins first: gmail.net -> gmail.com, not mail.net
      if (cost < bestCost) {
        bestCost = cost;
        best = `${provider}.${target}`;
      }
    });
    if (!best || best === typed) return null;
    return `${email.slice(0, at + 1)}${best}`;
  };

  // Sets a value the way typing does, so a framework-controlled field (the Forms popup) keeps it.
  const setTypedValue = (input, value) => {
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    setter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  };

  const fillSuggestion = (node, suggestion, held = false) => {
    // Leaving the field to tap the suggestion fires blur first; redrawing then would swallow the tap.
    const shown = node.querySelector('[data-email-suggestion]');
    if (!held && suggestion && shown && shown.getAttribute('data-email-suggestion') === suggestion) return;
    node.textContent = '';
    if (!suggestion) return;
    node.append('Did you mean ');
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = suggestion;
    button.setAttribute('data-email-suggestion', suggestion);
    button.style.cssText = 'font:inherit;color:inherit;background:none;border:0;padding:0;margin:0;'
      + 'text-decoration:underline;text-underline-offset:2px;cursor:pointer;';
    node.append(button, '?');
    if (held) node.append(document.createElement('br'), 'Tap it to fix it, or press the button again to keep yours.');
  };

  // True when this submit should be held once: a likely typo the shopper has not been shown at submit yet.
  const holdForTypo = (holder, value) => {
    const suggestion = suggestEmail(value);
    if (!suggestion || holder.dataset.museoriHeld === value) return null;
    holder.dataset.museoriHeld = value;
    return suggestion;
  };

  const readBag = (root) => {
    const node = root.querySelector('[data-save-bag-data]');
    try {
      return node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      return {};
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-save-bag-form]');
    if (!form) return;
    event.preventDefault();

    const root = form.closest('[data-save-bag]');
    const input = form.querySelector('input[type="email"]');
    const status = form.querySelector('[data-save-bag-status]');
    const button = form.querySelector('button[type="submit"]');
    const email = (input.value || '').trim();

    const typo = holdForTypo(form, email);
    if (typo) {
      fillSuggestion(status, typo, true);
      return;
    }

    if (!EMAIL_RE.test(email)) {
      status.textContent = 'Enter a valid email address.';
      input.focus();
      return;
    }

    button.disabled = true;
    status.textContent = 'Saving...';
    const bag = readBag(root);

    try {
      await post('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Cart save bag',
            profile: {
              data: {
                type: 'profile',
                attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: LIST_ID } } },
        },
      });

      await post('events', {
        data: {
          type: 'event',
          attributes: {
            properties: {
              items: bag.items || [],
              bag_url: bag.bag_url,
              item_count: bag.item_count,
              ...sourceProperties(),
            },
            value: bag.value,
            value_currency: bag.currency || 'USD',
            unique_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            metric: { data: { type: 'metric', attributes: { name: 'Saved Bag' } } },
            profile: { data: { type: 'profile', attributes: { email } } },
          },
        },
      });

      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ email });
      }

      root.classList.add('cart-save-bag--done');
      status.textContent = 'Saved. Check your inbox for a link back to your bag.';
    } catch (error) {
      button.disabled = false;
      status.textContent = 'Something went wrong. Please try again.';
    }
  });

  // Save-bag field: the typo line goes in the form's own status line (role=status, so it is also read out).
  let saveBagTimer = 0;
  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!input.matches || !input.matches('[data-save-bag-form] input[type="email"]')) return;
    const status = input.closest('[data-save-bag-form]').querySelector('[data-save-bag-status]');
    window.clearTimeout(saveBagTimer);
    saveBagTimer = window.setTimeout(() => fillSuggestion(status, suggestEmail(input.value)), SUGGEST_DELAY_MS);
  });
  document.addEventListener('focusout', (event) => {
    const input = event.target;
    if (!input.matches || !input.matches('[data-save-bag-form] input[type="email"]')) return;
    const status = input.closest('[data-save-bag-form]').querySelector('[data-save-bag-status]');
    window.clearTimeout(saveBagTimer);
    if (!status.textContent || status.querySelector('[data-email-suggestion]')) fillSuggestion(status, suggestEmail(input.value));
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-save-bag-status] [data-email-suggestion]');
    if (!button) return;
    const form = button.closest('[data-save-bag-form]');
    const input = form.querySelector('input[type="email"]');
    input.value = button.getAttribute('data-email-suggestion');
    form.querySelector('[data-save-bag-status]').textContent = '';
    input.focus();
  });

  /*
   * Shopify Forms sign-up popup (app embed <shopify-forms-embed>, open shadow root). Shopify creates the customer and
   * syncs it to Klaviyo with no visit data, so a popup subscriber has no source. On a successful sign-up this
   * identifies the browser to Klaviyo and tracks "Popup Signup" with the same source properties as Saved Bag.
   * It never subscribes anyone: Shopify already does that, with the consent the shopper gave in its own form.
   * Success = the app's own state: its root element carries data-current-step = tease | form | success | error
   * (the Forms bundle's step enum, lowercased). Only "success" after a valid submit counts; "error", a close
   * (the app sets the host to display:none) or 30 s without success never do. One report per popup.
   */
  const POPUP_HOST = 'shopify-forms-embed';
  const POPUP_WAIT_MS = 30000;
  const watchedRoots = new WeakSet();

  const reportPopupSignup = (email, firstName, formId) => {
    const klaviyo = window.klaviyo;
    if (!klaviyo) return;
    const profile = firstName ? { email, first_name: firstName } : { email };
    const properties = { form_id: formId, form_type: 'Shopify Forms popup', ...sourceProperties() };
    if (typeof klaviyo.identify === 'function') {
      Promise.resolve(klaviyo.identify(profile))
        .then(() => klaviyo.track('Popup Signup', properties))
        .catch(() => {});
    } else if (typeof klaviyo.push === 'function') {
      klaviyo.push(['identify', profile]);
      klaviyo.push(['track', 'Popup Signup', properties]);
    }
  };

  /*
   * The Forms app ships its fields with autocomplete="off", so the browser never offers the shopper's saved name and
   * email and the address gets typed by thumb. Turn autofill on (attributes only; the app still owns the value and
   * its validation) and add the typo line under the email field. The app re-renders between steps, so a
   * MutationObserver re-applies this to each new input.
   */
  const POPUP_FIELDS = {
    first_name: { autocomplete: 'given-name', autocapitalize: 'words' },
    email: { autocomplete: 'email', autocapitalize: 'off', autocorrect: 'off', spellcheck: 'false' },
  };

  const popupHints = new WeakMap();

  const patchPopupFields = (root) => {
    Object.entries(POPUP_FIELDS).forEach(([id, attributes]) => {
      const input = root.querySelector(`input#${id}`);
      if (!input || input.dataset.museoriPatched) return;
      input.dataset.museoriPatched = '1';
      Object.entries(attributes).forEach(([name, value]) => input.setAttribute(name, value));
      if (id !== 'email') return;

      const hint = document.createElement('p');
      hint.setAttribute('role', 'status');
      hint.setAttribute('aria-live', 'polite');
      hint.style.cssText = 'margin:6px 0 0;font-size:13px;line-height:1.4;color:#202020;text-align:left;';
      (input.parentElement || input).insertAdjacentElement('afterend', hint);
      popupHints.set(input, hint);
      let timer = 0;
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fillSuggestion(hint, suggestEmail(input.value)), SUGGEST_DELAY_MS);
      });
      input.addEventListener('blur', () => {
        window.clearTimeout(timer);
        fillSuggestion(hint, suggestEmail(input.value));
      });
      hint.addEventListener('click', (event) => {
        const button = event.target.closest('[data-email-suggestion]');
        if (!button) return;
        setTypedValue(input, button.getAttribute('data-email-suggestion'));
        hint.textContent = '';
        input.focus();
      });
    });
  };

  const watchPopup = (host) => {
    const root = host.shadowRoot;
    if (!root || watchedRoots.has(root)) return;
    watchedRoots.add(root);
    try {
      patchPopupFields(root);
      new MutationObserver(() => patchPopupFields(root)).observe(root, { childList: true, subtree: true });
      root.addEventListener('submit', (event) => {
        const input = event.target && event.target.querySelector && event.target.querySelector('input#email');
        const hint = input && popupHints.get(input);
        if (!hint) return;
        const typo = holdForTypo(input, (input.value || '').trim());
        if (!typo) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        fillSuggestion(hint, typo, true);
      }, true);
    } catch (error) {
      // Never let the field help break the popup.
    }
    const formId = (host.id.match(/(\d+)$/) || [])[1] || '';
    let latestSubmit = 0;
    let reported = false;

    root.addEventListener('submit', (event) => {
      const form = event.target;
      if (reported || !form || form.tagName !== 'FORM') return;
      const emailInput = form.querySelector('#email, [data-testid="field-email"]');
      const email = ((emailInput && emailInput.value) || '').trim();
      if (!EMAIL_RE.test(email)) return;
      const nameInput = form.querySelector('#first_name, [data-testid="field-first_name"]');
      const firstName = ((nameInput && nameInput.value) || '').trim().slice(0, 100);
      const submitId = ++latestSubmit;
      const started = Date.now();

      const check = () => {
        if (reported || submitId !== latestSubmit) return;
        const appRoot = root.querySelector('[data-current-step]');
        const step = appRoot ? appRoot.getAttribute('data-current-step') : '';
        if (step === 'success') {
          reported = true;
          reportPopupSignup(email, firstName, formId);
          return;
        }
        if (step === 'error' || host.style.display === 'none' || Date.now() - started > POPUP_WAIT_MS) return;
        window.setTimeout(check, 250);
      };
      window.setTimeout(check, 250);
    }, true);
  };

  const findPopups = () => {
    try {
      document.querySelectorAll(POPUP_HOST).forEach(watchPopup);
    } catch (error) {
      // Never let source tracking break the page.
    }
  };
  findPopups();
  window.setInterval(findPopups, 2000);
})();
